import { memo, useCallback, type NamedExoticComponent } from 'react';
import { Box, Flex, Text, useBoolean } from '@chakra-ui/react';
import moment from 'moment';
import { useSelector } from 'react-redux';

import Edited from 'components/Icons/Edited';
import BuilderModeToggle from 'pages/BuilderModeV1/components/BuilderModeToggle';
import TimeScaleFilter from 'pages/PlanPageDetailsOPZ/Plan/Filters/TimeScaleFilter';
import { Scenario } from 'models/response/scenario.interface';
import { getBlockDetails } from 'redux/PlanPageSlice';
import { getAllScenarios, getBaseScenario } from 'redux/ScenarioSlice';
import { getPlanPageThemeColors } from 'redux/ThemeSlice';

import {
  PLAN_ACCENT_BLUE,
  PLAN_HEADER_HORIZONTAL_PADDING,
  PLAN_REVAMP_RADIUS_SM,
  PLAN_TEXT,
} from '../planPageRevampUi';

import PlanScenarioComparisonPopover from './PlanScenarioComparisonPopover';

interface Properties {
  refetchRequest: () => void;
}

const PlanPageTitleBar = ({ refetchRequest }: Properties) => {
  const blockDetail = useSelector(getBlockDetails);
  const scenarios = useSelector(getAllScenarios);
  const baseScenario = useSelector(getBaseScenario);
  const themeColors = useSelector(getPlanPageThemeColors);
  const canCompare = (scenarios?.length ?? 0) > 1;
  const [isScenarioComparisonOpen, setScenarioComparisonOpen] = useBoolean();

  const persistScenario = useCallback(
    (scenario: Scenario) => {
      if (!blockDetail?.model_id) return;
      const planFilters = localStorage.getItem('global-modal-filters');
      const data = planFilters ? JSON.parse(planFilters) : {};
      const modalFilters = data[blockDetail.model_id] || {};
      modalFilters.scenario = scenario;
      localStorage.setItem(
        'global-modal-filters',
        JSON.stringify({ ...data, [blockDetail.model_id]: modalFilters }),
      );
    },
    [blockDetail?.model_id],
  );

  const editedLabel = blockDetail?.last_modified_at
    ? moment.utc(blockDetail.last_modified_at).fromNow(false)
    : null;

  return (
    <Flex
      w='100%'
      px={PLAN_HEADER_HORIZONTAL_PADDING}
      py={4}
      alignItems='center'
      justifyContent='space-between'
      gap={4}
    >
      <Box minW={0}>
        <Text {...PLAN_TEXT.pageTitle} color={themeColors.textDark} noOfLines={1}>
          {blockDetail?.name ?? 'Block'}
        </Text>
        {editedLabel && (
          <Flex alignItems='center' gap={2} mt={2}>
            <Edited />
            <Text {...PLAN_TEXT.metaAccent} color={PLAN_ACCENT_BLUE}>
              {`Edited ${editedLabel}`}
            </Text>
          </Flex>
        )}
      </Box>

      <Flex alignItems='center' gap={3} flexShrink={0} flexWrap='wrap' justifyContent='flex-end'>
        {scenarios?.length > 0 && baseScenario ? (
          <Flex
            alignItems='stretch'
            border='1px solid'
            borderColor={themeColors.border}
            borderRadius={PLAN_REVAMP_RADIUS_SM}
            overflow='hidden'
            bg={themeColors.surface}
            h='40px'
            flexShrink={0}
          >
            {canCompare ? (
              <PlanScenarioComparisonPopover
                isOpen={isScenarioComparisonOpen}
                onOpen={setScenarioComparisonOpen.on}
                onClose={setScenarioComparisonOpen.off}
                refetchRequest={refetchRequest}
                persistScenario={persistScenario}
              />
            ) : (
              <Flex alignItems='center' gap={2} px={3} h='40px'>
                <Box w='8px' h='8px' borderRadius='full' bg={PLAN_ACCENT_BLUE} flexShrink={0} />
                <Text {...PLAN_TEXT.body} color={themeColors.textDark} noOfLines={1} minW={'136px'}>
                  {baseScenario.name}
                </Text>
              </Flex>
            )}
            <Box
              w='10px'
              bg={themeColors.surface}
              borderColor={themeColors.border}
              flexShrink={0}
            />
            <TimeScaleFilter variant='revamp' refetchRequest={refetchRequest} />
          </Flex>
        ) : null}

        <BuilderModeToggle />
      </Flex>
    </Flex>
  );
};

export default memo(PlanPageTitleBar) as NamedExoticComponent<Properties>;
